import React, { useState, useEffect} from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import FormattedSummary from './FormattedSummary';
import ReactDOM from 'react-dom';

function DemoCaseSimulation() {
  const { difficulty } = useParams();
  console.log("Difficulty:", difficulty);
  const [caseData, setCaseData] = useState(null);
  const [userInput, setUserInput] = useState('');
  const [proctorResponse, setProctorResponse] = useState('');
  const [audioSrc, setAudioSrc] = useState('');
  const [criticalActions, setCriticalActions] = useState([]);
  const [matchedActions, setMatchedActions] = useState([]);
  const [caseComplete, setCaseComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [images, setImages] = useState([]);
  const [currentImage, setCurrentImage] = useState(null);
  const [userInputs, setUserInputs] = useState([]);
  const [proctorOutputs, setProctorOutputs] = useState([]);
  const [sessionId] = useState(() => `demo_${Date.now()}`);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [summary, setSummary] = useState('');
  
    const togglePopup = () => {
      setIsPopupVisible(!isPopupVisible);
    };

  useEffect(() => {
    const fetchDemoCase = async () => {
      try {
        const response = await axios.get(`/cases/demo-case/${difficulty}`);
        setCaseData(response.data);
        setCriticalActions(response.data.criticalActions || []);
        setImages(response.data.images || []);
        setSummary(response.data.summary || '');
      } catch (error) {
        console.error('Error fetching demo case:', error);
      }
    };

    fetchDemoCase();
  }, [difficulty]);

  const handleInputSubmit = async () => {
    if (!userInput.trim()) return;

    setLoading(true);

    try {
      const response = await axios.post('/cases/demo-case/respond', {
        sessionId,
        userInput,
        isMuted,
        difficulty,
      });

      const { aiResponse, audioContent } = response.data;
      setProctorResponse(aiResponse);
      setProctorOutputs((prev) => [...prev, aiResponse]);
      setUserInputs((prev) => [...prev, userInput]);

      if (audioContent) {
        const audioBlob = new Blob(
          [Uint8Array.from(atob(audioContent), (c) => c.charCodeAt(0))],
          { type: 'audio/mp3' }
        );
        setAudioSrc(URL.createObjectURL(audioBlob));
      }

      // Check for case completion
      if (/case has ended/i.test(aiResponse)) {
        setCaseComplete(true);
        submitCaseCompletion();
      }
    } catch (error) {
      console.error('Error generating response:', error);
    } finally {
      setLoading(false);
      setUserInput('');
    }
  };

  const submitCaseCompletion = async () => {
    try {
      const response = await axios.post('/cases/demo-case/complete', {
        sessionId,
        userInputs,
        proctorOutputs,
        difficulty,
      });
      console.log(response.data);
      setMatchedActions(response.data.matchedActions);
      setCriticalActions(response.data.criticalActions);
      setCaseComplete(true);
      // Reset user and proctor inputs for the next attempt
      setUserInputs([]);
      setProctorOutputs([]);

    } catch (error) {
      console.error('Error completing case:', error);
    }
  };

  const abortSimulation = async () => {

    try {
      setLoading(true); // Show spinner
      // End the case by submitting completion
      await submitCaseCompletion(); // Call the existing completion logic
      setLoading(false); // Hide spinner
    } catch (error) {
      console.error('Error aborting simulation:', error);
    }


  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (!isMuted) setAudioSrc('');
  };

  const startRecording = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Sorry, your browser does not support speech recognition.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;

    recognition.start();

    recognition.onresult = (event) => {
      const spokenText = event.results[0][0].transcript;
      setUserInput(spokenText);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      alert('Speech recognition error: ' + event.error);
    };
  };

  const handleImageClick = (imageUrl) => {
    setCurrentImage(imageUrl);
  };

const hasImages = Array.isArray(images) && images.length > 0;

// Only easy/medium/hard demo cases use "Media 0" (stored as last image)
const usesMedia0 = ['easy', 'medium', 'hard'].includes(difficulty);

const media0Url = usesMedia0 && hasImages ? images[images.length - 1] : null;

// If using Media 0: Media 1..N excludes the last image
// Otherwise: Media 1..N should include ALL images
const filteredImages = hasImages
  ? (usesMedia0 ? images.slice(0, -1) : images)
  : [];


// Adjust this condition if you have a better case-type flag
// const isClinicalDecisionMaking = Number(caseNumber) < 1000;

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleInputSubmit();
    }
  };

  const renderResultsGrid = () => (
    <div className="results">
      <h3>Your Score</h3>
      <table className="critical-actions-grid">
        <thead>
          <tr>
            <th>Critical Action</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {criticalActions.map((action) => (
            <tr key={action}>
              <td>{action}</td>
              <td>{matchedActions.includes(action) ?                 <svg
                  className="small-checkmark"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 52 52"
                  width="20"
                  height="20"
                >
                  <circle
                    className="checkmark-circle-bg"
                    cx="26"
                    cy="26"
                    r="25"
                    fill="none"
                  />
                  <path
                    className="checkmark-check"
                    fill="none"
                    d="M14 27l7 7 15-15"
                  />
                </svg> : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <FormattedSummary summary={summary} />
    </div>
  );

  if (!caseData) {
    return <div>Loading demo...</div>;
  }

  return (
    <div className="container">
      <h1>{caseData.title}</h1>

      {['procedures', 'ultrasound', 'difficultconversation'].includes(difficulty) && (
        <div className="mb-4 p-2 bg-yellow-100 text-yellow-800 rounded">
          Please begin by clicking <strong>Media 1</strong> to review the Candidate Task Sheet. 
          Start the simulation by introducing yourself.
        </div>
      )}
      {['easy', 'medium', 'hard'].includes(difficulty) && !caseComplete && (
        <div className="mb-4 p-2 bg-yellow-100 text-yellow-800 rounded">
          Please review Media 0 (your task sheet), then introduce yourself to the proctor, and begin by asking which specific elements of the HPI you’d like to know. 
        </div>
      )}

      <div style={{ position: 'relative', padding: '20px' }}>
      {/* Top-right button */}
      {!caseComplete && (
      <button
        style={{
          position: 'absolute',
          top: '175px',
          right: '10px',
          padding: '10px',
          backgroundColor: '#007bff',
          color: '#fff',
          border: 'none',
          borderRadius: '5px',
          cursor: 'pointer',
        }}
        onClick={togglePopup}
      >
        Quick Guide
      </button>
      )}
      {/* Popup Image */}
      {isPopupVisible && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={togglePopup} // Close popup when clicking outside the image
        >
          <div
            style={{
              position: 'relative',
              backgroundColor: '#fff',
              padding: '20px',
              borderRadius: '10px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            }}
          >
            <button
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                backgroundColor: 'red',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                fontSize: '16px',
                cursor: 'pointer',
              }}
              onClick={togglePopup}
            >
              &times;
            </button>
{/* Popup PDF */}
{isPopupVisible && (
  <div
    style={{
      position: 'fixed',
      inset: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '12px',               // mobile breathing room
    }}
    onClick={togglePopup}
  >
    <div
      style={{
        position: 'relative',
        backgroundColor: '#fff',
        width: 'min(960px, 100%)',    // responsive width
        height: 'min(85vh, 900px)',   // responsive height
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
      }}
      onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside
    >
      {/* Header bar (mobile friendly) */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 12px',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <strong style={{ fontSize: '14px' }}>Quick Guide</strong>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {/* Open in new tab as a fallback (esp. iOS Safari) */}
          <a
            href="/QuickGuide2026.pdf"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: '13px',
              padding: '6px 10px',
              background: '#007bff',
              color: '#fff',
              borderRadius: '6px',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            Open Fullscreen
          </a>

          <button
            onClick={togglePopup}
            aria-label="Close"
            style={{
              backgroundColor: '#ef4444',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              width: '34px',
              height: '34px',
              fontSize: '18px',
              cursor: 'pointer',
              lineHeight: '34px',
            }}
          >
            ×
          </button>
        </div>
      </div>

      {/* PDF viewer area */}
      <iframe
        src="/QuickGuide2026.pdf#view=FitH"  // keep toolbar enabled; FitH helps mobile
        title="Quick Guide PDF"
        style={{
          width: '100%',
          height: 'calc(100% - 52px)', // subtract header height
          border: 'none',
        }}
      />
    </div>
  </div>
)}

          </div>
        </div>
      )}
    </div>

      {!caseComplete ? (
        <div>
          <textarea
            id="userInput"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Type your question here or Speak with mic button (desktop only)"
            rows="4"
            maxLength="1200"
            className="input-box"
          />
          <div className="input-info">
            <p className="text-gray-500">Inputs Used: {userInputs.length} / 40</p>
          </div>
          <div className="button-container">
            <button onClick={handleInputSubmit} className="btn submit-btn" disabled={loading}>
              {loading ? 'Processing...' : 'Submit'}
            </button>
            <button onClick={toggleMute} className="btn mute-btn">
              <img src={isMuted ? '/unmute-icon.svg' : '/mute-icon.svg'} alt={isMuted ? 'Unmute' : 'Mute'} />
            </button>
            <button onClick={startRecording} className="btn audio-btn">
              <img src="/audio-icon.svg" alt="Start Recording" />
            </button>
            <button onClick={abortSimulation} className="btn abort-btn">
              Abort
            </button>
          </div>

          {/* <div className="image-buttons">
            {images.map((image, index) => (
              <button
                key={index}
                onClick={() => handleImageClick(image)}
                className="btn btn-secondary m-2"
              >
                Media {index + 1}
              </button>
            ))}
          </div> */}
          <div className="image-buttons">
  {['easy', 'medium', 'hard'].includes(difficulty) && media0Url && (
    <button
      onClick={() => setCurrentImage(media0Url)}
      className="btn btn-secondary m-2"
    >
      Media 0
    </button>
  )}

  {filteredImages.map((url, index) => (
    <button
      key={index}
      onClick={() => setCurrentImage(url)}
      className="btn btn-secondary m-2"
    >
      Media {index + 1}
    </button>
  ))}
</div>
          {currentImage && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'rgba(0,0,0,0.8)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '1rem',
                zIndex: 1000,
              }}
            >
              <div style={{ position: 'relative', maxWidth: '800px', height: '100%', maxHeight: '90vh' }}>
                <button
                  onClick={() => setCurrentImage(null)}
                  style={{
                    position: 'absolute',
                    top: '-1.1rem',
                    right: '-1rem',
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '50%',
                    width: '35px',
                    height: '35px',
                    fontSize: '18px',
                    cursor: 'pointer',
                    zIndex: 1001,
                  }}
                >
                  &times;
                </button>

                {/* simplest: image only */}
                <img src={currentImage} alt="Case Media" style={{ maxWidth: '100%', maxHeight: '90vh' }} />
              </div>
            </div>
          )}

          <p>
            <strong>Proctor/Sim:</strong> {proctorResponse}
          </p>
          {audioSrc && !isMuted && <audio controls src={audioSrc} autoPlay />}
        </div>
      ) : (
        renderResultsGrid()
      )}

      {loading && (
        <div className="spinner-container">
          <img src="/spinner.gif" alt="Loading..." className="spinner" />
        </div>
      )}
    </div>
  );
}

export default DemoCaseSimulation;
